import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, Loader2, CheckCircle, User, Award } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { usePdfExtract } from "@/hooks/use-pdf-extract";
import { AdminUser, adminAPI, getUserData } from "@/lib/api";
import {
  getDomainForStrengthName,
  translateDomainName,
  translateStrengthName,
} from "@/lib/strength-i18n";

interface ExtractedData {
  userName: string;
  userEmail?: string;
  strengths: {
    rank: number;
    name: string;
    domain: string;
  }[];
  topDomains: string[];
  completionDate: string;
  fileName: string;
}

interface AdminPDFUploadProps {
  onUploadComplete?: () => void;
  initialTargetUserId?: string | null;
}

export default function AdminPDFUpload({ onUploadComplete, initialTargetUserId }: AdminPDFUploadProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { toast } = useToast();
  const currentUser = getUserData();
  const isAdmin = currentUser?.role === "admin";
  const { extractPdf, isExtracting } = usePdfExtract();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedTargetUserId, setSelectedTargetUserId] = useState(initialTargetUserId || "");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [targetUserMissing, setTargetUserMissing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isAdmin) return;

    const loadUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const response = await adminAPI.getUsers({ limit: 100 });
        setUsers(response.data || []);
      } catch (error) {
        toast({
          title: t("common.error"),
          description: t("pdfUpload.loadUsersFailed"),
          variant: "destructive",
        });
      } finally {
        setIsLoadingUsers(false);
      }
    };

    loadUsers();
  }, [isAdmin, toast, t]);

  useEffect(() => {
    if (initialTargetUserId) {
      setSelectedTargetUserId(initialTargetUserId);
    }
  }, [initialTargetUserId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isAdmin) return;

    const file = event.target.files?.[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
      setExtractedData(null);
    } else {
      toast({
        title: t('pdfUpload.invalidFile'),
        description: t('pdfUpload.selectPdf'),
        variant: "destructive",
      });
    }
  };

  const handleUploadAndExtract = async () => {
    if (!isAdmin) return;

    if (!selectedFile) return;

    if (!selectedTargetUserId) {
      setTargetUserMissing(true);
      toast({
        title: t("pdfUpload.selectTargetUserTitle"),
        description: t("pdfUpload.selectTargetUserDesc"),
        variant: "destructive",
      });
      return;
    }

    setTargetUserMissing(false);
    setUploadProgress(10);

    try {
      const selectedUser = users.find(user => user._id === selectedTargetUserId);
      // Call the real PDF extraction API
      const result = await extractPdf(selectedFile, { targetUserId: selectedTargetUserId });
      
      console.log('Extraction result:', result);
      setUploadProgress(100);

      if (result.success && result.data) {
        console.log('Extraction successful, data:', result.data);
        
        // Check if extractedData exists and has strengths array
        const extractedData = result.data.extractedData;
        if (!extractedData || !extractedData.strengths || !Array.isArray(extractedData.strengths)) {
          console.error('Invalid data structure: extractedData.strengths is not an array', result.data);
          toast({
            title: t("common.error"),
            description: t("pdfUpload.invalidExtractedData"),
            variant: "destructive",
          });
          setUploadProgress(0);
          return;
        }
        
        // Map API response to ExtractedData format
        const domainCounts: Record<string, number> = {};
        extractedData.strengths.slice(0, 10).forEach(s => {
          const domain = getDomainForStrengthName(s.strengthName);
          if (domain) {
            domainCounts[domain] = (domainCounts[domain] || 0) + 1;
          }
        });
        
        const topDomains = Object.entries(domainCounts)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 2)
          .map(([domain]) => domain);

        const extracted: ExtractedData = {
          userName: selectedUser?.name || result.data.username || extractedData.username || t("pdfUpload.unknownUser"),
          userEmail: selectedUser?.email || result.data.userEmail || undefined,
          strengths: extractedData.strengths.map(s => {
            const domain = getDomainForStrengthName(s.strengthName);
            return {
              rank: s.rank,
              name: s.strengthName,
              domain: domain || "Unknown"
            };
          }),
          topDomains: topDomains.length > 0 ? topDomains : (extractedData.domains || []).map(d => d.domainName),
          completionDate: new Date().toISOString().split('T')[0],
          fileName: selectedFile.name,
        };
        
        console.log('Setting extracted data:', extracted);
        setExtractedData(extracted);
        console.log('Opening preview modal...');
        setIsPreviewOpen(true);
      } else {
        console.log('Extraction failed or no data:', result);
        setUploadProgress(0);
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress(0);
    }
  };

  const handleSaveToDatabase = async () => {
    if (!isAdmin) return;

    if (!extractedData) return;

    // Extraction already stored the PDF and saved ranks on the backend.
    setIsPreviewOpen(false);
    setSelectedFile(null);
    setSelectedTargetUserId("");
    setExtractedData(null);
    
    // Reset file input
    const fileInput = document.getElementById('pdf-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
    
    // Notify parent component to refresh the reports list
    if (onUploadComplete) {
      onUploadComplete();
    }
  };

  const openPdfFilePicker = () => {
    if (isExtracting) return;
    fileInputRef.current?.click();
  };

  if (!isAdmin) {
    return null;
  }

  const getDomainColor = (domain: string) => {
    switch (domain) {
      case "Strategic Thinking":
        return "bg-primary/10 text-primary border-primary/25";
      case "Executing":
        return "bg-secondary/10 text-brand-green-dark border-secondary/25";
      case "Influencing":
        return "bg-warning/10 text-warning border-warning/25";
      case "Relationship Building":
        return "bg-brand-teal-dark/10 text-brand-teal-dark border-brand-teal-dark/25";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  return (
    <div className="space-y-6" dir={isRtl ? "rtl" : "ltr"}>
      <div>
        <h2 className="text-2xl font-bold mb-2">{t('pdfUpload.title')}</h2>
        <p className="text-muted-foreground">
          {t('pdfUpload.description')}
        </p>
      </div>

      <Card className="p-4 bg-card/50 backdrop-blur-sm border-border/50 sm:p-6">
        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="target-user">{t("pdfUpload.targetUser")}</Label>
            <Select
              value={selectedTargetUserId}
              onValueChange={(value) => {
                setSelectedTargetUserId(value);
                setTargetUserMissing(false);
              }}
              disabled={isExtracting || isLoadingUsers}
            >
              <SelectTrigger
                id="target-user"
                className={`bg-background ${targetUserMissing ? "border-destructive ring-1 ring-destructive" : ""}`}
              >
                <SelectValue placeholder={isLoadingUsers ? t("pdfUpload.loadingUsers") : t("pdfUpload.selectUser")} />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user._id} value={user._id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {targetUserMissing && (
              <p className="text-sm text-destructive">{t("pdfUpload.selectTargetUserDesc")}</p>
            )}
          </div>

          {/* Upload Area */}
          <div
            className="border-2 border-dashed border-border/50 rounded-lg p-4 text-center hover:border-primary/50 transition-colors cursor-pointer sm:p-8"
            onClick={openPdfFilePicker}
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center sm:h-16 sm:w-16">
                <Upload className="w-8 h-8 text-primary" />
              </div>
              <div>
                <Label
                  className="text-base font-semibold cursor-pointer hover:text-primary transition-colors sm:text-lg"
                >
                  {t('pdfUpload.clickToUpload')}
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('pdfUpload.fileNote')}
                </p>
              </div>
              <Input
                ref={fileInputRef}
                id="pdf-upload"
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={handleFileSelect}
                disabled={isExtracting}
              />
            </div>
          </div>

          {/* Selected File Info */}
          {selectedFile && (
            <Card className="p-4 bg-muted/50 border-border">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <FileText className="w-8 h-8 text-primary" />
                  <div className="min-w-0">
                    <p className="break-all font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={handleUploadAndExtract}
                  disabled={isExtracting || !selectedTargetUserId}
                  className="w-full gap-2 sm:w-auto"
                >
                  {isExtracting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('pdfUpload.extracting')}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      {t('pdfUpload.extractData')}
                    </>
                  )}
                </Button>
              </div>
            </Card>
          )}

          {/* Upload Progress */}
          {isExtracting && (
            <Card className="p-4 bg-muted/50 border-border">
              <div className="space-y-3">
                <div className="flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:justify-between">
                  <span className="font-medium">
                    {t('pdfUpload.extractingData')}
                  </span>
                  <span className="text-muted-foreground">
                    {uploadProgress > 0 ? `${uploadProgress}%` : t('pdfUpload.processing')}
                  </span>
                </div>
                <Progress value={uploadProgress > 0 ? uploadProgress : undefined} className="h-2" />
                <p className="text-xs text-muted-foreground">
                  {t('pdfUpload.aiAnalyzing')}
                </p>
              </div>
            </Card>
          )}

          {/* Instructions */}
          <Card className="p-4 bg-primary/5 border-primary/20">
            <div className="flex items-start gap-3">
              <Award className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="space-y-2">
                <h3 className="font-semibold text-sm">{t('pdfUpload.whatExtracted')}</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• {t('pdfUpload.extractItem1')}</li>
                  <li>• {t('pdfUpload.extractItem2')}</li>
                  <li>• {t('pdfUpload.extractItem3')}</li>
                  <li>• {t('pdfUpload.extractItem4')}</li>
                </ul>
                <p className="text-xs text-muted-foreground mt-3">
                  <strong>{t("common.note")}:</strong> {t('pdfUpload.extractNote')}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </Card>

      {/* Preview and Save Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              <CheckCircle className="w-5 h-5 text-success" />
              {t('pdfUpload.dataExtracted')}
            </DialogTitle>
            <DialogDescription>
              {t('pdfUpload.reviewData')}
            </DialogDescription>
          </DialogHeader>

          {extractedData && (
            <div className="space-y-6">
              {/* User Info */}
              <Card className="p-4 bg-muted/50 border-border">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-4 h-4 text-primary" />
                    <h3 className="font-semibold">{t('pdfUpload.userInfo')}</h3>
                  </div>
                  <div>
                    <Label>{t("pdfUpload.targetUser")}</Label>
                    <Input
                      value={users.find(user => user._id === selectedTargetUserId)?.email || extractedData.userEmail || ""}
                      readOnly
                      className="bg-background"
                    />
                  </div>
                  <div>
                    <Label htmlFor="userName">{t('pdfUpload.userName')}</Label>
                    <Input
                      id="userName"
                      value={extractedData.userName}
                      onChange={(e) => setExtractedData({ ...extractedData, userName: e.target.value })}
                      className="bg-background"
                      placeholder={t("pdfUpload.userNamePlaceholder")}
                    />
                  </div>
                  <div>
                    <Label>{t('pdfUpload.completionDate')}</Label>
                    <Input
                      value={new Date(extractedData.completionDate).toLocaleDateString()}
                      readOnly
                      className="bg-background"
                    />
                  </div>
                </div>
              </Card>

              {/* Top Domains */}
              <div>
                <h3 className="font-semibold mb-3">{t('pdfUpload.topDomains')}</h3>
                <div className="flex flex-wrap gap-2">
                  {extractedData.topDomains.map((domain, index) => (
                    <Badge key={index} className={`${getDomainColor(domain)} text-sm py-2 px-3 sm:px-4`}>
                      #{index + 1} {translateDomainName(domain, t, i18n.language)}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* All 34 Strengths */}
              <div>
                <h3 className="font-semibold mb-3">{t('pdfUpload.allStrengths')}</h3>
                <div className="max-h-[400px] overflow-y-auto">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {extractedData.strengths.map((strength) => (
                      <div
                        key={strength.rank}
                        className="flex items-center gap-3 p-2 bg-muted/30 rounded border border-border/50"
                      >
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold">{strength.rank}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium break-words">
                            {translateStrengthName(strength.name, t, i18n.language)}
                          </p>
                          <Badge
                            variant="secondary"
                            className={`${getDomainColor(strength.domain)} text-xs`}
                          >
                            {translateDomainName(strength.domain, t, i18n.language)}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsPreviewOpen(false);
                setExtractedData(null);
              }}
            >
              {t('pdfUpload.cancel')}
            </Button>
            <Button 
              onClick={handleSaveToDatabase} 
              className="gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              {t('pdfUpload.saveToDatabase')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
